import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleMap, MarkerF, InfoWindowF, PolygonF } from "@react-google-maps/api";
import { useGoogleMaps } from "../../context/GoogleMapsContext";
import { Icon } from "@iconify/react";
import api from "../../services/api";
import { showToast } from "../../helper/notifications";

const MAP_CONTAINER_STYLE = {
  width: "100%",
  height: "580px",
  borderRadius: "12px",
};

/**
 * Modern curated color palette for Top Locations boundaries
 */
const TOP_LOCATION_PALETTE = [
  { fill: "#3B82F6", stroke: "#1D4ED8", name: "Blue" },
  { fill: "#10B981", stroke: "#047857", name: "Emerald" },
  { fill: "#F59E0B", stroke: "#B45309", name: "Amber" },
  { fill: "#8B5CF6", stroke: "#6D28D9", name: "Purple" },
  { fill: "#EC4899", stroke: "#BE185D", name: "Pink" },
  { fill: "#06B6D4", stroke: "#0E7490", name: "Cyan" },
  { fill: "#F97316", stroke: "#C2410C", name: "Orange" },
  { fill: "#14B8A6", stroke: "#0F766E", name: "Teal" },
  { fill: "#6366F1", stroke: "#4338CA", name: "Indigo" },
  { fill: "#84CC16", stroke: "#4D7C0F", name: "Lime" },
];

/**
 * Robust normalizer for geometrics_outline into array of coordinate paths
 */
const normalizePolygons = (data) => {
  if (!data) return [];
  let parsed;
  try {
    parsed = typeof data === "string" ? JSON.parse(data) : data;
  } catch {
    return [];
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return [];

  const cleanPath = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map((pt) => ({ lat: Number(pt?.lat), lng: Number(pt?.lng) }))
      .filter((pt) => !isNaN(pt.lat) && !isNaN(pt.lng));

  if (parsed[0] && typeof parsed[0] === "object" && Array.isArray(parsed[0].path)) {
    return parsed.map((p) => cleanPath(p.path)).filter((p) => p.length >= 3);
  }
  if (parsed.every((p) => Array.isArray(p))) {
    return parsed.map((p) => cleanPath(p)).filter((p) => p.length >= 3);
  }
  if (parsed[0] && typeof parsed[0] === "object" && parsed[0].lat !== undefined) {
    const single = cleanPath(parsed);
    return single.length >= 3 ? [single] : [];
  }
  return [];
};

/**
 * Creates a modern custom SVG Pin Marker for Google Maps
 */
const createCustomMarkerIcon = (color = "#4F46E5", isMoved = false) => {
  if (!window.google) return null;

  return {
    path: "M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20c0-6.627-5.373-12-12-12zm0 16c-2.209 0-4-1.791-4-4s1.791-4 4-4 4 1.791 4 4-1.791 4-4 4z",
    fillColor: isMoved ? "#D97706" : color,
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: "#FFFFFF",
    scale: isMoved ? 1.4 : 1.25,
    anchor: new window.google.maps.Point(12, 32),
    labelOrigin: new window.google.maps.Point(12, 44),
  };
};

const getLat = (loc) => {
  if (!loc) return NaN;
  const val = loc.latitude ?? loc.lat ?? loc.location_lat;
  return val !== null && val !== undefined && val !== "" ? parseFloat(val) : NaN;
};

const getLng = (loc) => {
  if (!loc) return NaN;
  const val = loc.longitude ?? loc.lng ?? loc.location_lng;
  return val !== null && val !== undefined && val !== "" ? parseFloat(val) : NaN;
};

const LocationMapView = ({ 
  locations = [], 
  loading = false, 
  getViewRoute = (item) => `/google-business/${item.id}/view`,
  allowPinDrag = false,
  onRefresh = () => {},
  onSaveCoordinates = null,
}) => {
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMaps();
  const mapRef = useRef(null);

  // Map state (Defaults to Satellite / Hybrid)
  const [zoomLevel, setZoomLevel] = useState(10);
  const [selectedMarker, setSelectedMarker] = useState(null);

  // Labels visibility state (persisted to localStorage)
  const [showLabels, setShowLabels] = useState(() => {
    try {
      const saved = localStorage.getItem("loqation_map_show_labels");
      return saved !== null ? saved === "true" : true;
    } catch {
      return true;
    }
  });

  // Location Outlines visibility state (persisted to localStorage)
  const [showLocationOutlines, setShowLocationOutlines] = useState(() => {
    try {
      const saved = localStorage.getItem("loqation_map_show_location_outlines");
      return saved !== null ? saved === "true" : false;
    } catch {
      return false;
    }
  });
  const [fallbackMainOutlines, setFallbackMainOutlines] = useState([]);

  // Track dragged / moved markers for manual saving (NO autosave)
  const [movedMarkers, setMovedMarkers] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Top Locations Boundaries State
  const [topLocations, setTopLocations] = useState([]);
  const [loadingTopLocations, setLoadingTopLocations] = useState(false);
  const [selectedBoundaryIds, setSelectedBoundaryIds] = useState(() => {
    try {
      const saved = localStorage.getItem("loqation_map_selected_top_boundaries");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return new Set(parsed);
      }
    } catch (e) {
      console.error("Failed to parse saved boundaries:", e);
    }
    return new Set();
  });
  const [boundaryDropdownOpen, setBoundaryDropdownOpen] = useState(false);
  const [boundarySearch, setBoundarySearch] = useState("");
  const [selectedBoundaryInfo, setSelectedBoundaryInfo] = useState(null);
  const boundaryDropdownRef = useRef(null);

  // Close boundary dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        boundaryDropdownRef.current &&
        !boundaryDropdownRef.current.contains(event.target)
      ) {
        setBoundaryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch Top Locations with valid geofence outlines
  useEffect(() => {
    let isMounted = true;
    const fetchTopLocations = async () => {
      setLoadingTopLocations(true);
      try {
        const res = await api.get("/top-locations", { params: { limit: 250 } });
        const rawList = res.data?.data || [];
        const parsed = rawList
          .map((item, idx) => {
            const polys = normalizePolygons(item.geometrics_outline);
            return {
              ...item,
              polygons: polys,
              color: TOP_LOCATION_PALETTE[idx % TOP_LOCATION_PALETTE.length],
            };
          })
          .filter((item) => item.polygons && item.polygons.length > 0);

        if (isMounted) {
          setTopLocations(parsed);
        }
      } catch (err) {
        console.error("Failed to load top locations for map boundaries:", err);
      } finally {
        if (isMounted) setLoadingTopLocations(false);
      }
    };
    fetchTopLocations();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch main locations outlines as fallback if passed `locations` lack `geometrics_outline`
  useEffect(() => {
    const hasDirectOutlineField = locations.some(
      (loc) => loc && Object.prototype.hasOwnProperty.call(loc, "geometrics_outline")
    );
    if (hasDirectOutlineField) return;

    let isMounted = true;
    const fetchMainOutlines = async () => {
      try {
        const res = await api.get("/main-locations", { params: { limit: 250 } });
        if (isMounted) {
          setFallbackMainOutlines(res.data?.data || []);
        }
      } catch (err) {
        console.error("Failed to load main location outlines:", err);
      }
    };
    fetchMainOutlines();
    return () => {
      isMounted = false;
    };
  }, [locations]);

  // Compute all valid Location Building Outlines
  const locationOutlines = useMemo(() => {
    const sourceList = locations.some(
      (loc) => loc && Object.prototype.hasOwnProperty.call(loc, "geometrics_outline")
    )
      ? locations
      : fallbackMainOutlines;

    return sourceList
      .map((item, idx) => {
        const polys = normalizePolygons(item.geometrics_outline);
        return {
          id: item.location_id || item.id || idx,
          name: item.location_name || item.title || item.name || `Location #${item.location_id || item.id}`,
          polygons: polys,
          color: TOP_LOCATION_PALETTE[idx % TOP_LOCATION_PALETTE.length],
          item,
        };
      })
      .filter((entry) => entry.polygons && entry.polygons.length > 0);
  }, [locations, fallbackMainOutlines]);

  // Persist selected boundaries to localStorage
  const updateSelectedBoundaries = (newSet) => {
    setSelectedBoundaryIds(newSet);
    try {
      localStorage.setItem(
        "loqation_map_selected_top_boundaries",
        JSON.stringify(Array.from(newSet))
      );
    } catch (e) {
      console.error("Failed to save selected boundaries:", e);
    }
  };

  const handleToggleBoundary = (id) => {
    const next = new Set(selectedBoundaryIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    updateSelectedBoundaries(next);
  };

  const handleSelectAllBoundaries = () => {
    const allIds = new Set(topLocations.map((t) => t.id));
    updateSelectedBoundaries(allIds);
  };

  const handleDeselectAllBoundaries = () => {
    updateSelectedBoundaries(new Set());
  };

  // Save label preference
  const handleToggleLabels = (checked) => {
    setShowLabels(checked);
    try {
      localStorage.setItem("loqation_map_show_labels", String(checked));
    } catch (e) {
      console.error(e);
    }
  };

  // Save location outlines preference
  const handleToggleLocationOutlines = (checked) => {
    setShowLocationOutlines(checked);
    try {
      localStorage.setItem("loqation_map_show_location_outlines", String(checked));
    } catch (e) {
      console.error(e);
    }
  };

  // Parse location rows containing valid lat/lng
  const validLocations = useMemo(() => {
    return locations.filter((item) => {
      const lat = getLat(item);
      const lng = getLng(item);
      return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
    });
  }, [locations]);

  // Marker icons
  const defaultMarkerIcon = useMemo(() => {
    return isLoaded ? createCustomMarkerIcon("#4F46E5", false) : null;
  }, [isLoaded]);

  const activeMarkerIcon = useMemo(() => {
    return isLoaded ? createCustomMarkerIcon("#E11D48", false) : null;
  }, [isLoaded]);

  const movedMarkerIcon = useMemo(() => {
    return isLoaded ? createCustomMarkerIcon("#D97706", true) : null;
  }, [isLoaded]);

  // Auto-fit map boundaries to visible markers and selected boundaries
  const fitMapToBounds = useCallback(() => {
    if (!mapRef.current || !window.google) return;

    const bounds = new window.google.maps.LatLngBounds();
    let count = 0;

    validLocations.forEach((loc) => {
      const moved = movedMarkers[loc.location_id || loc.id];
      const lat = moved ? moved.newLat : getLat(loc);
      const lng = moved ? moved.newLng : getLng(loc);
      bounds.extend({ lat, lng });
      count++;
    });

    topLocations
      .filter((topLoc) => selectedBoundaryIds.has(topLoc.id))
      .forEach((topLoc) => {
        topLoc.polygons.forEach((path) => {
          path.forEach((pt) => {
            bounds.extend({ lat: pt.lat, lng: pt.lng });
            count++;
          });
        });
      });

    if (showLocationOutlines) {
      locationOutlines.forEach((locOutline) => {
        locOutline.polygons.forEach((path) => {
          path.forEach((pt) => {
            bounds.extend({ lat: pt.lat, lng: pt.lng });
            count++;
          });
        });
      });
    }

    if (count === 1 && validLocations.length === 1) {
      const item = validLocations[0];
      const moved = movedMarkers[item.location_id || item.id];
      const lat = moved ? moved.newLat : getLat(item);
      const lng = moved ? moved.newLng : getLng(item);
      mapRef.current.setCenter({ lat, lng });
      mapRef.current.setZoom(15);
      setZoomLevel(15);
      return;
    }

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, 60);
      setTimeout(() => {
        if (mapRef.current) {
          const z = mapRef.current.getZoom();
          if (z !== undefined) setZoomLevel(z);
        }
      }, 200);
    }
  }, [validLocations, movedMarkers, topLocations, selectedBoundaryIds, showLocationOutlines, locationOutlines]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setZoomLevel(map.getZoom() || 10);
    fitMapToBounds();
  }, [fitMapToBounds]);

  const handleZoomChanged = useCallback(() => {
    if (mapRef.current) {
      const z = mapRef.current.getZoom();
      if (z !== undefined) setZoomLevel(z);
    }
  }, []);

  useEffect(() => {
    if (mapRef.current && validLocations.length > 0 && Object.keys(movedMarkers).length === 0) {
      fitMapToBounds();
    }
  }, [validLocations, fitMapToBounds, movedMarkers]);

  const defaultCenter = useMemo(() => {
    if (validLocations.length > 0) {
      const first = validLocations[0];
      return {
        lat: getLat(first),
        lng: getLng(first),
      };
    }
    return { lat: 51.1657, lng: 10.4515 }; // Germany center default
  }, [validLocations]);

  // Pin Drag Handlers (Master Locations)
  const handleMarkerDragEnd = (item, e) => {
    if (!allowPinDrag || !e.latLng) return;

    const newLat = Number(e.latLng.lat().toFixed(7));
    const newLng = Number(e.latLng.lng().toFixed(7));
    const key = item.location_id || item.id;

    setMovedMarkers((prev) => ({
      ...prev,
      [key]: {
        location_id: item.location_id || item.id,
        id: item.id,
        location_name: item.location_name || item.title || item.name || `Location #${key}`,
        store_code: item.store_code,
        originalLat: getLat(item),
        originalLng: getLng(item),
        newLat,
        newLng,
        item,
      },
    }));

    showToast("info", `Pin for "${item.location_name || item.title || 'Location'}" moved. Click 'Save Positions' to save changes.`);
  };

  // Discard all moved pin changes
  const handleDiscardMoves = () => {
    setMovedMarkers({});
    showToast("info", "Changes to pin positions discarded.");
  };

  // Save all moved pin changes (explicit button click required - NO autosave)
  const handleSaveMoves = async () => {
    const movedList = Object.values(movedMarkers);
    if (movedList.length === 0) return;

    setIsSaving(true);
    try {
      if (onSaveCoordinates) {
        await onSaveCoordinates(movedList);
      } else {
        // Default batch update for Master Locations
        const payload = {
          locations: movedList.map((m) => ({
            location_id: m.location_id,
            lat: m.newLat,
            lng: m.newLng,
          })),
        };
        const res = await api.post("/main-locations/batch-update", payload);
        if (res.data?.success) {
          showToast(
            "success",
            `${movedList.length} pin position${movedList.length > 1 ? "s" : ""} saved successfully!`
          );
        } else {
          throw new Error(res.data?.message || "Failed to save positions");
        }
      }

      setMovedMarkers({});
      onRefresh();
    } catch (error) {
      console.error("Save pin positions error:", error);
      showToast("error", error.response?.data?.message || error.message || "Failed to save coordinates.");
    } finally {
      setIsSaving(false);
    }
  };

  const movedCount = Object.keys(movedMarkers).length;

  // Map display options (Default to Satellite / Hybrid)
  const mapOptions = useMemo(() => ({
    mapTypeId: "hybrid",
    mapTypeControl: true,
    streetViewControl: false,
    fullscreenControl: true,
    zoomControl: true,
    styles: showLabels ? [] : [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      }
    ]
  }), [showLabels]);

  if (!isLoaded || loading) {
    return (
      <div 
        className="w-100 radius-12 bg-neutral-100 d-flex align-items-center justify-content-center flex-column" 
        style={{ height: "580px", border: "2px dashed #e3e6ef" }}
      >
        <div className="d-flex justify-content-center align-items-center py-80">
          <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-3xl me-2" />
          <p className="fw-medium text-secondary-light">Loading Google Maps...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card radius-12 p-16 shadow-sm border-0 bg-base">
      {/* MAP TOP CONTROLS & HEADER */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-16 pb-12 border-bottom">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <h6 className="fw-bold mb-0 text-neutral-800">Google Maps View</h6>

          {/* Live Zoom Label */}
          <div className="badge bg-neutral-100 text-neutral-800 border px-10 py-6 radius-8 d-inline-flex align-items-center gap-1">
            <Icon icon="solar:magnifer-zoom-in-linear" className="text-primary-600 text-sm" />
            <span className="fw-semibold text-xs">Zoom: {zoomLevel}</span>
          </div>

          {/* Satellite Default Badge */}
          <span className="badge bg-neutral-100 text-neutral-700 border px-8 py-6 radius-8 d-inline-flex align-items-center gap-1 text-xs">
            <Icon icon="solar:earth-bold" className="text-info-main text-sm" />
            Satellite
          </span>

          {/* Draggable Pins Mode Indicator */}
          {allowPinDrag && (
            <span className="badge bg-primary-50 text-primary-600 border border-primary-200 px-10 py-6 radius-8 d-inline-flex align-items-center gap-1 text-xs">
              <Icon icon="solar:cursor-square-bold" className="text-primary-600 text-sm" />
              Pin Drag Active
            </span>
          )}
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          {/* Top Locations Boundaries Multi-Select Dropdown */}
          <div className="position-relative" ref={boundaryDropdownRef}>
            <button
              type="button"
              className={`btn btn-sm radius-8 px-12 py-6 d-flex align-items-center gap-2 border ${
                selectedBoundaryIds.size > 0
                  ? "btn-primary-50 text-primary-600 border-primary-300 shadow-sm"
                  : "btn-outline-neutral-300 bg-white text-neutral-700"
              }`}
              onClick={() => setBoundaryDropdownOpen((prev) => !prev)}
              title="Toggle Top Location Boundaries"
            >
              <Icon icon="solar:layers-minimalistic-bold" className="text-base text-primary-600" />
              <span className="text-xs fw-semibold">Boundaries</span>
              {topLocations.length > 0 && (
                <span
                  className={`badge radius-4 px-6 py-2 text-2xs ${
                    selectedBoundaryIds.size > 0
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200 text-neutral-700"
                  }`}
                >
                  {selectedBoundaryIds.size === topLocations.length && topLocations.length > 0
                    ? "All"
                    : `${selectedBoundaryIds.size}/${topLocations.length}`}
                </span>
              )}
              <Icon
                icon={boundaryDropdownOpen ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"}
                className="text-xs ms-1 text-neutral-500"
              />
            </button>

            {/* Dropdown Menu Popover */}
            {boundaryDropdownOpen && (
              <div
                className="position-absolute end-0 mt-2 bg-white radius-12 shadow-lg border p-12"
                style={{ width: "300px", zIndex: 1050 }}
              >
                <div className="d-flex align-items-center justify-content-between pb-8 mb-8 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <Icon icon="solar:layers-minimalistic-bold" className="text-primary-600 text-base" />
                    <span className="text-xs fw-bold text-neutral-900">Top Location Boundaries</span>
                  </div>
                  <span className="text-2xs text-secondary-light">
                    {selectedBoundaryIds.size} active
                  </span>
                </div>

                {/* Filter Search Input */}
                {topLocations.length > 3 && (
                  <div className="mb-8">
                    <input
                      type="text"
                      className="form-control form-control-sm text-xs radius-6"
                      placeholder="Filter boundaries..."
                      value={boundarySearch}
                      onChange={(e) => setBoundarySearch(e.target.value)}
                    />
                  </div>
                )}

                {/* Quick Action Buttons */}
                <div className="d-flex align-items-center justify-content-between gap-2 mb-8 px-4">
                  <button
                    type="button"
                    className="btn btn-2xs btn-light text-primary-600 fw-semibold text-2xs p-1 radius-4 border-0"
                    onClick={handleSelectAllBoundaries}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-2xs btn-light text-neutral-600 text-2xs p-1 radius-4 border-0"
                    onClick={handleDeselectAllBoundaries}
                  >
                    Clear Selection
                  </button>
                </div>

                {/* Checkbox List */}
                <div
                  className="d-flex flex-column gap-1 overflow-auto pe-1"
                  style={{ maxHeight: "230px" }}
                >
                  {topLocations.length === 0 ? (
                    <div className="text-center py-16 text-secondary-light text-2xs">
                      {loadingTopLocations ? "Loading boundaries..." : "No Top Location boundaries found."}
                    </div>
                  ) : (
                    topLocations
                      .filter((t) =>
                        t.name.toLowerCase().includes(boundarySearch.toLowerCase())
                      )
                      .map((topLoc) => {
                        const isChecked = selectedBoundaryIds.has(topLoc.id);
                        return (
                          <label
                            key={topLoc.id}
                            className={`d-flex align-items-center justify-content-between p-6 radius-6 cursor-pointer user-select-none transition-all ${
                              isChecked ? "bg-primary-50 text-neutral-900" : "hover-bg-neutral-50 text-neutral-700"
                            }`}
                            style={{ fontSize: "12px" }}
                          >
                            <div className="d-flex align-items-center gap-2 overflow-hidden me-2">
                              <input
                                type="checkbox"
                                className="form-check-input mt-0 cursor-pointer"
                                checked={isChecked}
                                onChange={() => handleToggleBoundary(topLoc.id)}
                              />
                              <span
                                className="w-10-px h-10-px rounded-circle flex-shrink-0"
                                style={{ backgroundColor: topLoc.color.fill }}
                              />
                              <span className="text-truncate fw-medium" title={topLoc.name}>
                                {topLoc.name}
                              </span>
                            </div>
                            <span className="badge bg-neutral-100 text-neutral-600 text-2xs px-4 py-1 radius-4 flex-shrink-0">
                              {topLoc.polygons.length} poly
                            </span>
                          </label>
                        );
                      })
                  )}
                </div>

                {/* Location Outlines Quick Toggle inside Boundaries Menu */}
                <div className="pt-8 mt-8 border-top">
                  <label
                    className={`d-flex align-items-center justify-content-between p-6 radius-6 cursor-pointer user-select-none transition-all ${
                      showLocationOutlines ? "bg-primary-50 text-neutral-900" : "hover-bg-neutral-50 text-neutral-700"
                    }`}
                    style={{ fontSize: "12px" }}
                  >
                    <div className="d-flex align-items-center gap-2 overflow-hidden me-2">
                      <input
                        type="checkbox"
                        className="form-check-input mt-0 cursor-pointer"
                        checked={showLocationOutlines}
                        onChange={(e) => handleToggleLocationOutlines(e.target.checked)}
                      />
                      <Icon icon="mdi:vector-polygon" className="text-primary-600 text-sm flex-shrink-0" />
                      <span className="text-truncate fw-semibold">
                        All Location Outlines
                      </span>
                    </div>
                    <span className="badge bg-neutral-100 text-neutral-600 text-2xs px-6 py-1 radius-4 flex-shrink-0">
                      {locationOutlines.length}
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Show / Hide All Location Outlines Toggle */}
          <div
            className={`form-check form-switch d-flex align-items-center gap-2 mb-0 px-12 py-6 radius-8 border ${
              showLocationOutlines ? "bg-primary-50 border-primary-300" : "bg-neutral-50"
            }`}
          >
            <input
              className="form-check-input cursor-pointer mt-0"
              type="checkbox"
              id="toggle-location-outlines"
              checked={showLocationOutlines}
              onChange={(e) => handleToggleLocationOutlines(e.target.checked)}
            />
            <label
              className="form-check-label text-xs fw-semibold text-neutral-700 cursor-pointer user-select-none d-inline-flex align-items-center gap-1"
              htmlFor="toggle-location-outlines"
              title="Display building outlines for all locations"
            >
              <Icon icon="mdi:vector-polygon" className="me-1 text-sm text-primary-600" />
              <span>Outlines {showLocationOutlines ? "On" : "Off"}</span>
              {locationOutlines.length > 0 && (
                <span
                  className={`badge radius-4 px-6 py-2 text-2xs ms-1 ${
                    showLocationOutlines
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200 text-neutral-700"
                  }`}
                >
                  {locationOutlines.length}
                </span>
              )}
            </label>
          </div>

          {/* Show / Hide Map Labels Toggle */}
          <div className="form-check form-switch d-flex align-items-center gap-2 mb-0 bg-neutral-50 px-12 py-6 radius-8 border">
            <input
              className="form-check-input cursor-pointer mt-0"
              type="checkbox"
              id="toggle-map-labels"
              checked={showLabels}
              onChange={(e) => handleToggleLabels(e.target.checked)}
            />
            <label
              className="form-check-label text-xs fw-semibold text-neutral-700 cursor-pointer user-select-none"
              htmlFor="toggle-map-labels"
            >
              <Icon icon="solar:tag-linear" className="me-1 text-sm text-neutral-500" />
              Labels {showLabels ? "On" : "Off"}
            </label>
          </div>

          {/* Recenter button */}
          <button
            type="button"
            className="btn btn-sm btn-outline-neutral-300 bg-white text-neutral-700 radius-8 px-10 py-6 d-flex align-items-center gap-1 text-xs"
            onClick={fitMapToBounds}
            title="Fit map to markers and boundaries"
          >
            <Icon icon="solar:maximize-square-linear" className="text-sm" />
            <span>Recenter</span>
          </button>

          <span className="text-xs text-secondary-light">
            <strong>{validLocations.length}</strong> of <strong>{locations.length}</strong> with coordinates
          </span>
        </div>
      </div>

      {/* PENDING COORDINATES SAVE BANNER (FOR PIN DRAG - NO AUTOSAVE) */}
      {movedCount > 0 && (
        <div className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between p-12 radius-8 mb-16 border-0 shadow-sm gap-3">
          <div className="d-flex align-items-center gap-2">
            <Icon icon="solar:danger-triangle-bold" className="text-warning-main text-xl flex-shrink-0" />
            <div>
              <span className="fw-bold text-sm text-neutral-900">
                {movedCount} pin position{movedCount > 1 ? "s" : ""} moved
              </span>
              <span className="text-xs text-neutral-700 ms-2">
                (Changes are not yet saved to the database)
              </span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-neutral-600 bg-white radius-8 text-xs px-12 py-6"
              onClick={handleDiscardMoves}
              disabled={isSaving}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary radius-8 text-xs px-16 py-6 d-flex align-items-center gap-2 shadow-sm"
              onClick={handleSaveMoves}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon icon="solar:diskette-bold" className="text-base" />
                  Save Positions
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* GOOGLE MAP */}
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={defaultCenter}
        zoom={10}
        mapTypeId="hybrid"
        onLoad={onMapLoad}
        onZoomChanged={handleZoomChanged}
        options={mapOptions}
      >
        {/* TOP LOCATIONS BOUNDARIES (MULTI-SELECTED POLYGONS) */}
        {topLocations
          .filter((topLoc) => selectedBoundaryIds.has(topLoc.id))
          .map((topLoc) => {
            return topLoc.polygons.map((path, pIdx) => (
              <PolygonF
                key={`top-boundary-${topLoc.id}-${pIdx}`}
                paths={path}
                options={{
                  fillColor: topLoc.color.fill,
                  fillOpacity: 0.22,
                  strokeColor: topLoc.color.stroke,
                  strokeWeight: 2.5,
                  strokeOpacity: 0.9,
                  clickable: true,
                  zIndex: 2,
                }}
                onClick={() => {
                  const centerPt = path[0] || { lat: 0, lng: 0 };
                  setSelectedBoundaryInfo({
                    topLoc,
                    position: centerPt,
                  });
                }}
              />
            ));
          })}

        {/* LOCATION BUILDING OUTLINES (ALL LOCATIONS WHEN TOGGLED ON) */}
        {showLocationOutlines &&
          locationOutlines.map((locOutline) =>
            locOutline.polygons.map((path, pIdx) => (
              <PolygonF
                key={`loc-outline-${locOutline.id}-${pIdx}`}
                paths={path}
                options={{
                  fillColor: locOutline.color.fill,
                  fillOpacity: 0.28,
                  strokeColor: locOutline.color.stroke,
                  strokeWeight: 2.5,
                  strokeOpacity: 0.95,
                  clickable: true,
                  zIndex: 5,
                }}
                onClick={() => {
                  const hasCoords = !isNaN(getLat(locOutline.item)) && !isNaN(getLng(locOutline.item));
                  setSelectedMarker(
                    hasCoords
                      ? locOutline.item
                      : { ...locOutline.item, lat: path[0]?.lat || 0, lng: path[0]?.lng || 0 }
                  );
                }}
              />
            ))
          )}

        {/* INFO WINDOW FOR SELECTED TOP LOCATION BOUNDARY */}
        {selectedBoundaryInfo && (
          <InfoWindowF
            position={selectedBoundaryInfo.position}
            onCloseClick={() => setSelectedBoundaryInfo(null)}
          >
            <div className="p-2" style={{ maxWidth: "260px" }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span
                  className="w-12-px h-12-px rounded-circle d-inline-block flex-shrink-0"
                  style={{ backgroundColor: selectedBoundaryInfo.topLoc.color.fill }}
                />
                <h6 className="fw-bold text-sm mb-0">
                  {selectedBoundaryInfo.topLoc.name}
                </h6>
              </div>
              <span className="badge bg-primary-50 text-primary-600 text-2xs mb-2">
                Top Location Boundary ({selectedBoundaryInfo.topLoc.polygons.length} Geofence{selectedBoundaryInfo.topLoc.polygons.length > 1 ? "s" : ""})
              </span>
              {selectedBoundaryInfo.topLoc.description && (
                <p className="text-xs text-neutral-600 mb-8">
                  {selectedBoundaryInfo.topLoc.description}
                </p>
              )}
              <button
                type="button"
                className="btn btn-xs btn-outline-primary w-100 text-xs radius-4 d-flex align-items-center justify-content-center gap-1"
                onClick={() => navigate(`/locations-top/${selectedBoundaryInfo.topLoc.id}/edit`)}
              >
                <Icon icon="solar:pen-2-linear" className="text-xs" />
                Edit Geofence
              </button>
            </div>
          </InfoWindowF>
        )}

        {/* MARKERS */}
        {validLocations.map((item, index) => {
          const itemKey = item.location_id || item.id || item.google_location_id || item.place_id || index;
          const moved = movedMarkers[item.location_id || item.id];
          
          const lat = moved ? moved.newLat : getLat(item);
          const lng = moved ? moved.newLng : getLng(item);

          const isSelected = selectedMarker === item || 
            (selectedMarker?.id && item.id && selectedMarker.id === item.id) || 
            (selectedMarker?.location_id && item.location_id && selectedMarker.location_id === item.location_id) ||
            (selectedMarker?.google_location_id && item.google_location_id && selectedMarker.google_location_id === item.google_location_id) ||
            (selectedMarker?.place_id && item.place_id && selectedMarker.place_id === item.place_id);

          const rawTitle = item.location_name || item.title || item.name || "Location";
          const displayLabel = rawTitle.length > 25 ? `${rawTitle.slice(0, 22)}...` : rawTitle;

          return (
            <MarkerF
              key={itemKey}
              position={{ lat, lng }}
              draggable={allowPinDrag}
              onDragEnd={(e) => handleMarkerDragEnd(item, e)}
              icon={moved ? movedMarkerIcon : (isSelected ? activeMarkerIcon : defaultMarkerIcon)}
              onClick={() => setSelectedMarker(item)}
              zIndex={10}
              label={
                showLabels
                  ? {
                      text: displayLabel,
                      className: "map-marker-pin-label",
                    }
                  : undefined
              }
            />
          );
        })}

        {/* INFO WINDOW ON CLICK */}
        {selectedMarker && (
          <InfoWindowF
            position={{
              lat: movedMarkers[selectedMarker.location_id || selectedMarker.id]?.newLat || getLat(selectedMarker),
              lng: movedMarkers[selectedMarker.location_id || selectedMarker.id]?.newLng || getLng(selectedMarker),
            }}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div className="p-2" style={{ maxWidth: "250px" }}>
              <h6 className="fw-bold text-sm mb-1">
                {selectedMarker.title || selectedMarker.location_name || selectedMarker.name || "Location"}
              </h6>

              {movedMarkers[selectedMarker.location_id || selectedMarker.id] && (
                <div className="badge bg-warning-focus text-warning-main mb-2 text-2xs px-8 py-4 radius-4">
                  ⚠️ Position moved (unsaved)
                </div>
              )}

              {selectedMarker.is_verified !== undefined && (
                <div className="mb-2">
                  {(() => {
                    const isConf =
                      selectedMarker.is_confirmed === true ||
                      ["VERIFIED", "CONFIRMED", "TRUE", "1"].includes(
                        String(selectedMarker.is_verified || "").toUpperCase()
                      );
                    const isPend =
                      selectedMarker.confirmation_status === "PENDING" ||
                      String(selectedMarker.is_verified || "").toUpperCase() === "PENDING";
                    return (
                      <span
                        className={`badge radius-4 text-2xs px-8 py-4 d-inline-flex align-items-center gap-1 ${
                          isConf
                            ? "bg-success-focus text-success-main"
                            : isPend
                              ? "bg-warning-focus text-warning-main"
                              : "bg-danger-focus text-danger-main"
                        }`}
                      >
                        <Icon
                          icon={
                            isConf
                              ? "solar:verified-check-bold"
                              : isPend
                                ? "solar:clock-circle-bold"
                                : "solar:close-circle-bold"
                          }
                        />
                        <span>{isConf ? "Confirmed" : isPend ? "Pending" : "Not Confirmed"}</span>
                      </span>
                    );
                  })()}
                </div>
              )}

              {(selectedMarker.primary_category_name || selectedMarker.business_category || selectedMarker.store_code || selectedMarker.code || selectedMarker.internal_id) && (
                <div className="mb-2">
                  <span className="badge bg-primary-100 text-primary-600 radius-4 text-xs">
                    {selectedMarker.primary_category_name || selectedMarker.business_category 
                      ? (selectedMarker.primary_category_name || selectedMarker.business_category)
                      : `Code: ${selectedMarker.store_code || selectedMarker.code || selectedMarker.internal_id}`}
                  </span>
                </div>
              )}

              <p className="text-xs text-neutral-600 mb-8">
                {selectedMarker.formatted_address || 
                 (selectedMarker.city 
                  ? `${selectedMarker.city}, ${selectedMarker.country_code || selectedMarker.country || selectedMarker.state || ''}` 
                  : selectedMarker.address_line1 || selectedMarker.street_address || selectedMarker.address || "No address provided")}
              </p>

              <div className="d-flex align-items-center gap-1 text-2xs text-secondary-light mb-8">
                <Icon icon="solar:map-point-linear" />
                <span>
                  {(movedMarkers[selectedMarker.location_id || selectedMarker.id]?.newLat || getLat(selectedMarker)).toFixed(5)}, 
                  {(movedMarkers[selectedMarker.location_id || selectedMarker.id]?.newLng || getLng(selectedMarker)).toFixed(5)}
                </span>
              </div>

              <button
                className="btn btn-xs btn-primary w-100 text-xs radius-4 d-flex align-items-center justify-content-center gap-1"
                onClick={() => navigate(getViewRoute(selectedMarker))}
              >
                View Details
              </button>
            </div>
          </InfoWindowF>
        )}
      </GoogleMap>
    </div>
  );
};

export default LocationMapView;